import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { BookOpen, Plus, Pencil, Trash2, X, GripVertical } from 'lucide-react'
import { DEFAULT_COURSE_TYPES, mockAdminCourses, mockAdminMentors, type AdminCourse, type CourseModule } from '@/data/admin-mock-data'
import { toast } from 'sonner'

const CATEGORIES = ['Complete Package', 'Physics', 'Chemistry', 'Biology'] as const
const FEATURE_OPTIONS = ['Live Classes', 'Doubt Clearing', 'Test Series', 'Study Material', 'HD Videos', 'Practice Problems', 'Weekly Tests'] as const

const emptyModule = (): CourseModule => ({ id: `mod-${Date.now()}`, title: '', lectures: 0, hours: 0 })

export function AdminCoursesView() {
    const [courses, setCourses] = useState<AdminCourse[]>(mockAdminCourses)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [courseTypes, setCourseTypes] = useState<string[]>([...DEFAULT_COURSE_TYPES])
    const [newTypeInput, setNewTypeInput] = useState('')

    const [form, setForm] = useState<Partial<AdminCourse>>({
        title: '',
        category: 'Complete Package',
        type: 'Long Term',
        instructors: [{ name: '', title: '' }],
        rating: 4.9,
        reviews: 0,
        students: 0,
        lectures: 0,
        duration: '',
        features: [],
        modules: [],
        aboutInstructor: '',
        studentsTaught: '',
        lastUpdated: new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
        price: '',
        originalPrice: '',
        discountPercent: 0,
        isActive: true,
    })

    const addNewCourseType = () => {
        const t = newTypeInput.trim()
        if (t && !courseTypes.includes(t)) {
            setCourseTypes(prev => [...prev, t])
            setForm(f => ({ ...f, type: t }))
            setNewTypeInput('')
        }
    }

    const openCreate = () => {
        setEditingId(null)
        setForm({
            title: '',
            category: 'Complete Package',
            type: 'Long Term',
            instructors: [{ name: '', title: '' }],
            rating: 4.9,
            reviews: 0,
            students: 0,
            lectures: 0,
            duration: '',
            features: [],
            modules: [],
            aboutInstructor: '',
            studentsTaught: '',
            lastUpdated: new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
            price: '',
            originalPrice: '',
            discountPercent: 0,
            isActive: true,
        })
        setModalOpen(true)
    }

    const openEdit = (c: AdminCourse) => {
        setEditingId(c.id)
        setForm({ ...c })
        setModalOpen(true)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!form.title || !form.instructors?.length || !form.price) {
            toast.error('Fill required fields: Title, At least one Instructor, Price')
            return
        }
        if (editingId) {
            setCourses(prev => prev.map(c => c.id === editingId ? { ...c, ...form } as AdminCourse : c))
        } else {
            setCourses(prev => [...prev, { ...form, id: `c-${Date.now()}`, mentorIds: [], createdAt: new Date().toISOString().split('T')[0] } as AdminCourse])
        }
        toast.success(editingId ? 'Course updated' : 'Course published')
        setModalOpen(false)
    }

    const handleDelete = (id: string) => {
        if (confirm('Remove this course?')) setCourses(prev => prev.filter(c => c.id !== id))
    }

    const toggleFeature = (f: string) => {
        setForm(prev => ({
            ...prev,
            features: prev.features?.includes(f) ? prev.features.filter(x => x !== f) : [...(prev.features || []), f],
        }))
    }

    const addModule = () => {
        setForm(prev => ({ ...prev, modules: [...(prev.modules || []), emptyModule()] }))
    }

    const updateModule = (idx: number, field: keyof CourseModule, value: string | number) => {
        setForm(prev => {
            const mods = [...(prev.modules || [])]
            mods[idx] = { ...mods[idx], [field]: value }
            return { ...prev, modules: mods }
        })
    }

    const removeModule = (idx: number) => {
        setForm(prev => ({ ...prev, modules: prev.modules?.filter((_, i) => i !== idx) || [] }))
    }

    // Instructor Helpers
    const addInstructor = () => {
        setForm(prev => ({ ...prev, instructors: [...(prev.instructors || []), { name: '', title: '' }] }))
    }

    const removeInstructor = (idx: number) => {
        setForm(prev => ({ ...prev, instructors: prev.instructors?.filter((_, i) => i !== idx) || [] }))
    }

    const updateInstructor = (idx: number, field: 'name' | 'title', value: string) => {
        setForm(prev => {
            const insts = [...(prev.instructors || [])]
            insts[idx] = { ...insts[idx], [field]: value }

            // If name matches a mentor, try to set mentor ID (simplified logic)
            if (field === 'name') {
                const mentor = mockAdminMentors.find(m => m.name === value)
                if (mentor) insts[idx].id = mentor.id
                else delete insts[idx].id
            }

            return { ...prev, instructors: insts }
        })
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Manage Courses</h2>
                    <p className="text-sm text-theme-secondary">Publish full course details: curriculum, pricing, features, and instructor info</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors">
                    <Plus className="h-5 w-5" />
                    Publish Course
                </button>
            </div>

            <div className="space-y-3">
                {courses.map((c) => (
                    <GlassmorphicCard key={c.id} className="p-5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <BookOpen className="h-8 w-8 text-primary-blue shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-semibold text-theme-primary">{c.title}</h3>
                                    <p className="text-sm text-theme-secondary">
                                        {c.instructors.map(i => i.name).join(', ')} · {c.type} · {c.category}
                                    </p>
                                    <div className="flex gap-2 mt-1 text-xs text-theme-muted">
                                        <span>{c.rating} ({c.reviews} reviews)</span>
                                        <span>{c.students} students</span>
                                        <span>{c.lectures} lectures</span>
                                        <span>{c.duration}</span>
                                    </div>
                                    <p className="text-sm font-semibold text-primary-blue mt-1">{c.price} {c.originalPrice && <span className="line-through text-theme-muted">{c.originalPrice}</span>}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700' : 'bg-slate-200 text-theme-secondary'}`}>{c.isActive ? 'Active' : 'Inactive'}</span>
                                <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-theme-secondary"><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => handleDelete(c.id)} className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    </GlassmorphicCard>
                ))}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-2xl p-6 shadow-xl my-8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold text-theme-primary">{editingId ? 'Edit Course' : 'Publish Course'}</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Basic info */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Title *</label>
                                    <input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="Complete NEET 2025 Preparation - Physics, Chemistry & Biology" required />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Category</label>
                                        <select value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary">
                                            {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Course Type</label>
                                        <div className="flex gap-2">
                                            <select value={form.type || ''} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="flex-1 px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary">
                                                {courseTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                            <input value={newTypeInput} onChange={e => setNewTypeInput(e.target.value)} placeholder="Add new" className="w-24 px-2 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" />
                                            <button type="button" onClick={addNewCourseType} className="px-2 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-theme-primary text-sm">+</button>
                                        </div>
                                    </div>
                                </div>

                                {/* Instructors Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-theme-secondary">Instructors *</label>
                                        <button type="button" onClick={addInstructor} className="text-sm text-primary-blue hover:underline">+ Add Instructor</button>
                                    </div>
                                    <div className="space-y-2">
                                        {/* Use existing mentors for autocomplete/datalist */}
                                        <datalist id="mentor-list">
                                            {mockAdminMentors.map(m => <option key={m.id} value={m.name}>{m.subject}</option>)}
                                        </datalist>

                                        {(form.instructors || []).map((inst, idx) => (
                                            <div key={idx} className="flex flex-col sm:flex-row gap-2 bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                                                <div className="flex-1">
                                                    <input
                                                        list="mentor-list"
                                                        value={inst.name}
                                                        onChange={e => updateInstructor(idx, 'name', e.target.value)}
                                                        placeholder="Instructor Name (Select or type)"
                                                        className="w-full px-3 py-1.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                                    />
                                                </div>
                                                <div className="flex-1 flex gap-2">
                                                    <input
                                                        value={inst.title}
                                                        onChange={e => updateInstructor(idx, 'title', e.target.value)}
                                                        placeholder="Title (e.g. AIIMS Delhi)"
                                                        className="flex-1 px-3 py-1.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                                    />
                                                    {idx > 0 && (
                                                        <button type="button" onClick={() => removeInstructor(idx)} className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 self-center">
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <div><label className="block text-sm font-medium text-theme-secondary mb-1">Rating</label><input type="number" step="0.1" value={form.rating || ''} onChange={e => setForm(f => ({ ...f, rating: parseFloat(e.target.value) || 0 }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" /></div>
                                    <div><label className="block text-sm font-medium text-theme-secondary mb-1">Reviews</label><input type="number" value={form.reviews || ''} onChange={e => setForm(f => ({ ...f, reviews: parseInt(e.target.value) || 0 }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" /></div>
                                    <div><label className="block text-sm font-medium text-theme-secondary mb-1">Students</label><input type="number" value={form.students || ''} onChange={e => setForm(f => ({ ...f, students: parseInt(e.target.value) || 0 }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" /></div>
                                    <div><label className="block text-sm font-medium text-theme-secondary mb-1">Lectures</label><input type="number" value={form.lectures || ''} onChange={e => setForm(f => ({ ...f, lectures: parseInt(e.target.value) || 0 }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" /></div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Duration</label>
                                    <input value={form.duration || ''} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="12 months" />
                                </div>
                            </div>

                            {/* What You'll Get */}
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-2">What You'll Get</label>
                                <div className="flex flex-wrap gap-2">
                                    {FEATURE_OPTIONS.map(f => (
                                        <label key={f} className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={form.features?.includes(f) || false} onChange={() => toggleFeature(f)} className="rounded border-slate-300" />
                                            <span className="text-sm text-theme-secondary">{f}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Course Curriculum */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-theme-secondary">Course Curriculum</label>
                                    <button type="button" onClick={addModule} className="text-sm text-primary-blue hover:underline">+ Add Module</button>
                                </div>
                                <div className="space-y-2">
                                    {(form.modules || []).map((mod, idx) => (
                                        <div key={mod.id} className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <GripVertical className="h-4 w-4 text-slate-400 shrink-0" />
                                            <input value={mod.title} onChange={e => updateModule(idx, 'title', e.target.value)} placeholder="Module title" className="flex-1 px-3 py-1.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" />
                                            <input type="number" value={mod.lectures || ''} onChange={e => updateModule(idx, 'lectures', parseInt(e.target.value) || 0)} placeholder="Lectures" className="w-20 px-2 py-1.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" />
                                            <input type="number" step="0.5" value={mod.hours || ''} onChange={e => updateModule(idx, 'hours', parseFloat(e.target.value) || 0)} placeholder="Hrs" className="w-16 px-2 py-1.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" />
                                            <button type="button" onClick={() => removeModule(idx)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* About Instructor */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">About Instructor (General)</label>
                                    <input value={form.aboutInstructor || ''} onChange={e => setForm(f => ({ ...f, aboutInstructor: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="Expert Educator" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Students Taught</label>
                                        <input value={form.studentsTaught || ''} onChange={e => setForm(f => ({ ...f, studentsTaught: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="2,500+ students taught" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-theme-secondary mb-1">Last Updated</label>
                                        <input value={form.lastUpdated || ''} onChange={e => setForm(f => ({ ...f, lastUpdated: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="January 2025" />
                                    </div>
                                </div>
                            </div>

                            {/* Pricing */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Price *</label>
                                    <input value={form.price || ''} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="₹19,999" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Original Price</label>
                                    <input value={form.originalPrice || ''} onChange={e => setForm(f => ({ ...f, originalPrice: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="₹29,999" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Discount %</label>
                                    <input type="number" value={form.discountPercent || ''} onChange={e => setForm(f => ({ ...f, discountPercent: parseInt(e.target.value) || 0 }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="33" />
                                </div>
                            </div>

                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={form.isActive ?? true} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded border-slate-300" />
                                <span className="text-sm text-theme-secondary">Active (visible to students)</span>
                            </label>

                            <div className="flex gap-2 pt-4">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold">Publish Course</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
